<template>
  <div class="column bg-grey-1 full-height border-top-profiler">
    <div
      v-if="!isLoading"
      class="col-auto q-px-md q-py-sm border-bottom-details bg-grey-3"
    >
      <div class="row items-center">
        <div class="col">
          <span class="label-title">{{ description }}</span>
        </div>
        <div
          class="col-auto"
        >
          <q-btn
            v-if="isEmail"
            dense
            size="0.9em"
            padding="0em 0.7em"
            class="q-mr-sm"
            color="primary"
            flat
            unelevated
            @click="$emit('close-profiler', false)"
            label="Fechar"
          />
          <q-btn
            v-if="!isSaved"
            dense
            size="0.9em"
            padding="0em 0.7em"
            color="primary"
            unelevated
            @click="saveAnswers"
            :disabled="isLoading"
            label="Salvar"
          />
        </div>
      </div>
    </div>

    <span
      v-if="isEmail"
      class="text-center q-pt-xs q-pb-xs"
    >{{ profilerSelected.labelMessage }}</span>

    <div class="col">
      <div
        v-if="isLoading"
        class="flex full-height items-center justify-center q-my-lg"
      >
        <q-spinner-ios
          color="primary"
          size="3em"
        />
      </div>
      <q-scroll-area
        ref="scrollAreaRef"
        v-else
        class="full-height q-px-sm"
      >
        <div
          v-if="hasTree"
          class="row q-py-xs"
        >
          <FieldTree
            ref="treeRef"
            :tree="tree"
            :state="tree.state"
            :disabled="isSaved"
            v-model="tree.answers"
          />
        </div>

        <q-separator
          v-if="hasTree"
          class="q-my-sm"
        />

        <div
          v-if="fields"
          class="row items-start"
        >
          <template v-for="field in fields">
            <component
              :key="field.id"
              :is="field.component"
              :disabled="isSaved"
              :type="field.type"
              :label="field.title"
              :state="field.state"
              :configs="field.configs"
              v-model="field.answer"
            />
          </template>
        </div>
      </q-scroll-area>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import FieldTree from '@/components/fields/FieldTree'
import FieldText from '@/components/fields/FieldText'
import FieldDate from '@/components/fields/FieldDate'
import FieldFloat from '@/components/fields/FieldFloat'
import FieldSelect from '@/components/fields/FieldSelect'
import FieldButton from '@/components/fields/FieldButton'
import FieldInteger from '@/components/fields/FieldInteger'

export default {
  components: {
    FieldTree
  },

  props: {
    attendanceId: {},
    profilerSelected: {},
    isEmail: {
      type: Boolean,
      default: false
    },
    isPreview: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      tree: null,
      fields: null
    }
  },

  async mounted () {
    if (this.isEmail) {
      if (!this.profiler) {
        const { attendanceId } = this
        const messageId = this.profilerSelected.messageId
        await this.loadProfilerEmail({ attendanceId, messageId })
      }

      this.setTreesFields()
    }
  },

  computed: {
    ...mapState({
      profiler ({ attendanceModule }) {
        if (this.isEmail) return this.profilerEmail(attendanceModule)
        return attendanceModule.profilers[this.attendanceId]
      },

      isLoading ({ attendanceModule }) {
        const attendanceId = this.attendanceId
        if (this.isEmail) return Boolean(this.profilerEmail(attendanceModule)?.isLoading)
        return Boolean(attendanceModule.profilers[attendanceId]?.isLoading)
      },

      isSaved ({ attendanceModule }) {
        const attendanceId = this.attendanceId

        if (this.isEmail) {
          if (!this.profilerEmail(attendanceModule)) return true
          return Boolean(this.profilerEmail(attendanceModule)?.isSaved)
        }
        return Boolean(attendanceModule.profilers[attendanceId]?.isSaved)
      },

      description ({ attendanceModule }) {
        const attendanceId = this.attendanceId
        if (this.isEmail) return this.profilerEmail(attendanceModule)?.description
        return attendanceModule.profilers[attendanceId]?.description ?? ''
      },

      hasTree () {
        return this.tree && this.tree.children?.length
      }
    })
  },

  methods: {
    ...mapActions({
      setProfiler: 'attendanceModule/setProfiler',
      loadProfilerEmail: 'attendanceModule/loadProfiler',
      saveProfiler: 'attendanceModule/saveProfiler'
    }),

    saveAnswers () {
      if (!this.checkForm()) return
      if (this.isEmail) {
        const { attendanceId, profilerSelected } = this
        const { messageId } = profilerSelected
        return this.saveProfiler({ attendanceId, emailMessageIds: messageId })
      }
      this.saveProfiler(this.attendanceId)
    },

    checkForm () {
      let isValid = true

      const vue = this
      const hasFields = vue.fields && vue.fields.length
      const hasTree = vue.tree && vue.tree.children?.length

      if (hasFields) {
        for (const field of vue.fields) {
          const state = { ...field.state }
          state.dirty = true
          state.error = isRequiredField(field) && isEmptyField(field) ? 'Campo Obrigatório' : null
          if (state.error) isValid = false
          if (!isEqual(field.state, state)) field.state = state
        }
      }

      if (hasTree) {
        const state = { ...vue.tree.state }

        state.dirty = true
        state.error = vue.isValidTreeAnswers() ? null : 'Campo Obrigatório'
        if (state.error) isValid = false
        if (!isEqual(vue.tree.state, state)) vue.tree.state = state
      }

      return isValid
    },

    isValidTreeAnswers () {
      const vue = this
      const answers = Array.isArray(vue.tree.answers) ? vue.tree.answers : []
      const objTree = toObjTree(vue.tree.children)
      let isValid = Boolean(answers.length)

      for (const answer of answers) {
        const children = objTree[answer]?.children ?? []
        if (!children.length) continue
        const isPending = !children.find(({ id }) => answers.includes(id))
        if (isPending) isValid = false
      }

      return isValid
    },

    // animateScroll () {
    //   const { scrollAreaRef } = this.$refs
    //   if (scrollAreaRef) scrollAreaRef.setScrollPercentage(2, 500)
    // },

    setAnswers () {
      const tree = this.tree && Array.isArray(this.tree.answers) ? [...this.tree.answers] : []
      const fields = this.fields.reduce((acc, { id, answer }) => ({ ...acc, [id]: answer }), {})
      const attendanceId = this.attendanceId

      if (this.isEmail) {
        const messageId = this.handleEmailMessageId()
        return this.setProfiler({ attendanceId, messageId, tree, fields, isEmail: true })
      }

      this.setProfiler({ attendanceId, tree, fields })
    },

    setTreesFields () {
      const vue = this
      if (!vue.profiler) return
      if (!vue.profiler.id) return

      const setFields = this.isEmail ? true : !vue.fields
      const setTree = this.isEmail ? true : !vue.tree

      if (setFields) {
        vue.fields = vue.profiler.fields.map((field) => {
          const component = components[field.type]
          if (!component) return null

          const state = { dirty: false, error: null }
          const answer = [null, undefined, ''].includes(field.answer) ? null : field.answer
          return { ...field, component, state, answer }
        }).filter(v => v)
        for (const field of vue.fields) {
          this.$watch(() => field.answer, () => {
            const state = { ...field.state }
            state.dirty = true
            state.error = isRequiredField(field) && isEmptyField(field) ? 'Campo Obrigatório' : null
            if (!isEqual(field.state, state)) field.state = state
          }, { deep: true })
        }
      }

      if (setTree) {
        const state = { dirty: false, error: null }
        const answers = vue.profiler.tree && Array.isArray(vue.profiler.tree.answers) ? vue.profiler.tree.answers : []
        vue.tree = JSON.parse(JSON.stringify({ ...vue.profiler.tree, answers, state }))

        this.$watch(() => vue.tree.answers, () => {
          const state = { ...vue.tree.state }

          if (state.dirty) {
            state.error = vue.isValidTreeAnswers() ? null : 'Campo Obrigatório'
            if (!isEqual(vue.tree.state, state)) vue.tree.state = state
          }
        }, { deep: true })
      }
    },

    profilerEmail (attendanceModule) {
      const messageId = this.handleEmailMessageId()
      if (!attendanceModule.profilersEmail[this.attendanceId]) return
      return attendanceModule.profilersEmail[this.attendanceId][messageId]
    },

    handleEmailMessageId () {
      const { agentEmailMessageId, contactEmailMessageId } = this.profilerSelected.messageId
      return agentEmailMessageId || contactEmailMessageId
    }
  },

  watch: {
    tree: {
      deep: true,
      handler () {
        // this.animateScroll()
        this.setAnswers()
      }
    },

    fields: {
      deep: true,
      handler () {
        this.setAnswers()
      }
    },

    isLoading: {
      deep: true,
      handler (isLoading) {
        if (isLoading) return
        this.setTreesFields()
      }
    },

    profilerSelected: {
      deep: true,
      async handler () {
        if (!this.profiler) {
          const { attendanceId } = this
          const messageId = this.profilerSelected.messageId
          await this.loadProfilerEmail({ attendanceId, messageId })
        }
        this.setTreesFields()
      }
    }
  }
}

const isRequiredField = ({ configs }) => (
  ['true', 'yes', 'y', 't', '1'].includes(String(configs?.fieldRequired).toLowerCase())
)

const isEmptyField = ({ answer }) => (
  [null, undefined, ''].includes(answer) || !String(answer).trim()
)

const isEqual = (source, target) => (
  JSON.stringify(source) === JSON.stringify(target)
)

const toObjTree = (children) => {
  if (!Array.isArray(children)) return {}

  return children.reduce((acc, child) => {
    acc[child.id] = child
    return Object.assign(acc, toObjTree(child.children))
  }, {})
}

const components = {
  TEXT: FieldText,
  DATE: FieldDate,
  FLOAT: FieldFloat,
  RADIO: FieldSelect,
  SELECT: FieldSelect,
  INTEGER: FieldInteger,
  CHECKBOX: FieldSelect,
  MULTISELECT: FieldSelect,
  BUTTON: FieldButton
}
</script>

<style lang="sass" scoped>
$border-divisor: #E5E5E5

.btn-save
  border-radius: 2px
  border: none
  width: 81px !important
  height: 24px !important q-py-sm

.border-bottom-details
  border-bottom: 1px solid $border-divisor

.border-top-save
  border-top: 1px solid $border-divisor

.border-top-profiler
  border-top: 1px solid #CFD8DC

.label-title
  font-weight: 500
  font-size: 14px

.label-btn
  font-weight: normal
  font-size: 12px
</style>
